import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-learner-maps-listing',
  templateUrl: './learner-maps-listing.component.html',
  styleUrls: ['./learner-maps-listing.component.scss']
})
export class LearnerMapsListingComponent implements OnInit, OnDestroy {

  public env = environment;
  public allmaps: any[] = [];
  public learnerMapLookup: { [id: string]: any } = {};
  public imageCache: { [id: string]: string } = {};
  public placeholderImage = 'assets/images/learner-map-placeholder.svg';
  public dataReady = false;
  private assignedTitles = new Set<string>();

  private destroy$ = new Subject<void>();

  constructor(
    public http: HttpClient,
    public router: Router,
    public store: Store<any>
  ) {}

  ngOnInit() {
    this.fetchLearnerMapsFromAPI();
  }

  private fetchLearnerMapsFromAPI() {
    // Fetch learner maps directly from API
    this.http.get<any>(`${environment.profileURL}/api/v1/learner_maps`).pipe(
      takeUntil(this.destroy$),
      catchError((err) => {
        console.error('Error fetching learner maps:', err);
        this.dataReady = true;
        return of([]);
      })
    ).subscribe((response) => {
      const learnerMaps = response?.learner_maps ?? [];
      
      // Build assigned titles set and map entries
      this.assignedTitles = new Set<string>();
      const mapEntries: [string, any][] = [];
      const requests: any[] = [];

      learnerMaps.forEach((map: any) => {
        if (map?.title) this.assignedTitles.add(map.title);
        if (!map?.id) return;
        
        const normalizedMap = { ...map, value: map.id };
        mapEntries.push([map.id, normalizedMap]);
        
        // Fetch detailed map data for each learner map
        requests.push(
          this.http.get(`${environment.profileURL}/api/v1/learner_maps/${map.id}`).pipe(
            catchError(() => of(null))
          )
        );
      });

      this.allmaps = mapEntries;

      if (requests.length > 0) {
        forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe((results: any[]) => {
          results.forEach((res) => {
            if (res?.learner_map?.id) {
              this.learnerMapLookup[res.learner_map.id] = res.learner_map;
            }
          });
          this.buildImageCache();
          this.dataReady = true;
        });
      } else {
        this.dataReady = true;
      }
    });
  }

  getMapData(mapId: string): any {
    return this.learnerMapLookup[mapId] || null;
  }

  private buildImageCache() {
    this.allmaps.forEach(([, val]) => {
      if (val?.value) {
        this.cacheImage(val.value, val.title);
      }
    });
  }

  private cacheImage(mapId: string, fallbackTitle: string) {
    const data = this.learnerMapLookup[mapId];
    if (data?.image) {
      this.imageCache[mapId] = data.image;
    } else if (fallbackTitle) {
      this.imageCache[mapId] = `${this.env.s3_upload}/images/learner-maps/${fallbackTitle.toLowerCase().replace(/\s+/g, '-')}.jpg`;
    } else {
      this.imageCache[mapId] = this.placeholderImage;
    }
  }

  getCachedImage(mapId: string): string {
    return this.imageCache[mapId] || this.placeholderImage;
  }

  hasMap(title: string): boolean {
    return this.assignedTitles.has(title);
  }

  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img && img.src !== this.placeholderImage && !img.src.endsWith(this.placeholderImage)) {
      img.src = this.placeholderImage;
      const mapId = img.getAttribute('data-map-id');
      if (mapId) {
        this.imageCache[mapId] = this.placeholderImage;
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
